import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import API from '../utils/api';
import toast from 'react-hot-toast';

const fadeIn = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

// ===== ADMIN OVERVIEW =====
function AdminOverview({ stats, recentRequests, onViewRequest }) {
  if (!stats) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="stats-row">
        {[
          { icon: '👥', label: 'Total Users', value: stats.totalUsers, cls: 'blue' },
          { icon: '📝', label: 'Total Tasks', value: stats.totalRequests, cls: 'purple' },
          { icon: '🔓', label: 'Open Tasks', value: stats.openRequests, cls: 'yellow' },
          { icon: '✅', label: 'Resolved', value: stats.resolvedRequests, cls: 'green' },
        ].map((s, i) => (
          <motion.div key={i} className="stat-card" whileHover={{ y: -4 }}>
            <div className={`stat-icon ${s.cls}`}>{s.icon}</div>
            <div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </motion.div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700 }}>Recent Tasks</h3>
        <div style={{
          background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 8, padding: '6px 14px', color: '#34d399', fontSize: 14, fontWeight: 700
        }}>
          💰 Revenue: ${Number(stats.revenue || 0).toLocaleString()}
        </div>
      </div>

      <div className="table-card">
        {recentRequests?.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📭</div><h3>No tasks yet</h3></div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Title</th><th>User</th><th>Priority</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
            <tbody>
              {recentRequests?.map(r => (
                <tr key={r.id}>
                  <td>{r.title}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{r.user_name}</td>
                  <td><span className={`priority-${r.priority}`} style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.priority}</span></td>
                  <td><span className={`status-badge status-${r.status}`}>{r.status.replace('_', ' ')}</span></td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => onViewRequest(r)}>Respond →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </motion.div>
  );
}

// ===== ALL REQUESTS (ADMIN) =====
function AdminRequests({ onViewRequest }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    API.get('/requests').then(res => setRequests(res.data.requests)).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header">
          <h3>All Tasks ({filtered.length})</h3>
          <select className="form-control" style={{ width: 160, padding: '8px 12px', fontSize: 13 }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        {filtered.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📭</div><h3>No tasks</h3></div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Title</th><th>User</th><th>Category</th><th>Priority</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td>{r.title}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{r.user_name}<br /><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{r.user_email}</span></td>
                  <td style={{ color: 'var(--text-muted)' }}>{r.category}</td>
                  <td><span className={`priority-${r.priority}`} style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.priority}</span></td>
                  <td><span className={`status-badge status-${r.status}`}>{r.status.replace('_', ' ')}</span></td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => onViewRequest(r)}>Manage →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </motion.div>
  );
}

// ===== ADMIN REQUEST DETAIL =====
function AdminRequestDetail({ request, onBack }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(request.status);
  const [users, setUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState(request.assigned_to || '');

  useEffect(() => {
    API.get(`/requests/${request.id}`).then(res => setResponses(res.data.responses)).finally(() => setLoading(false));
    API.get('/admin/users').then(res => setUsers(res.data.users.filter(u => u.role === 'admin')));
  }, [request.id]);

  const updateStatus = async () => {
    try {
      await API.put(`/requests/${request.id}/status`, { status, assigned_to: assignedTo || null });
      toast.success('Status updated!');
    } catch { toast.error('Failed to update status'); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('message', reply);
      if (file) fd.append('file', file);
      const res = await API.post(`/requests/${request.id}/respond`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResponses(prev => [...prev, res.data.response]);
      setReply('');
      setFile(null);
      toast.success('Response sent!');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); } finally { setSending(false); }
  };

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={onBack}>← Back to Tasks</button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24, alignItems: 'start' }}>
        <div>
          <div className="glass-card" style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{request.title}</h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <span className={`status-badge status-${request.status}`}>{request.status.replace('_', ' ')}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                By: <strong style={{ color: 'var(--text-secondary)' }}>{request.user_name}</strong>
              </span>
            </div>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>{request.description}</p>
            {request.file_name && (
              <a href={`http://localhost:5000/uploads/${request.file_path}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}>
                📎 {request.file_name}
              </a>
            )}
          </div>

          {loading ? <div className="loading-container"><div className="loading-spinner" /></div> : (
            <div className="response-list">
              {responses.map(r => (
                <div key={r.id} className={`response-item ${r.is_admin_response ? 'admin-response' : ''}`}>
                  <div className="response-author">
                    <div className={`response-avatar ${!r.is_admin_response ? 'user-avatar' : ''}`}>
                      {r.author_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div className="response-name">
                        {r.author_name}
                        {r.is_admin_response && <span style={{ marginLeft: 8, background: 'rgba(99,102,241,0.15)', color: 'var(--brand-primary)', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>Admin</span>}
                      </div>
                      <div className="response-time">{new Date(r.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <p className="response-message">{r.message}</p>
                  {r.file_name && (
                    <a href={`http://localhost:5000/uploads/${r.file_path}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}>
                      📎 {r.file_name}
                    </a>
                  )}
                </div>
              ))}

              <div className="glass-card">
                <h4 style={{ marginBottom: 16, fontSize: 15, fontWeight: 700 }}>📤 Admin Response</h4>
                <form onSubmit={sendReply}>
                  <div className="form-group">
                    <textarea className="form-control" placeholder="Write your expert response..." value={reply} onChange={e => setReply(e.target.value)} style={{ minHeight: 120 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => document.getElementById('admin-file').click()}>
                      📎 {file ? file.name : 'Attach File'}
                    </button>
                    <input id="admin-file" type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                    <button type="submit" className="btn btn-primary btn-sm" disabled={sending}>
                      {sending ? '⏳ Sending...' : '🚀 Send Response'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar - manage request */}
        <div className="glass-card" style={{ position: 'sticky', top: 80 }}>
          <h4 style={{ marginBottom: 20, fontSize: 15, fontWeight: 700 }}>⚙️ Manage Task</h4>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-control" value={status} onChange={e => setStatus(e.target.value)}>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Assign To</label>
            <select className="form-control" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
              <option value="">Unassigned</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={updateStatus}>
            💾 Update
          </button>

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              <div style={{ marginBottom: 4 }}>📅 Created: {new Date(request.created_at).toLocaleString()}</div>
              <div>🏷️ Category: {request.category}</div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ===== USERS MANAGEMENT =====
function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    Promise.all([API.get('/admin/users'), API.get('/admin/plans')])
      .then(([uRes, pRes]) => { setUsers(uRes.data.users); setPlans(pRes.data.plans); })
      .finally(() => setLoading(false));
  };

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/admin/users/${editUser.id}`, editUser);
      toast.success('User updated!');
      setEditUser(null);
      loadData();
    } catch { toast.error('Failed to update'); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await API.delete(`/admin/users/${id}`);
      toast.success('User deleted');
      loadData();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header"><h3>All Users ({users.length})</h3></div>
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Plan</th><th>Requests</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td style={{ color: 'var(--text-muted)' }}>{u.email}</td>
                <td>
                  <span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--brand-primary)', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                    {u.plan_name || 'No Plan'}
                  </span>
                </td>
                <td style={{ color: 'var(--text-muted)' }}>{u.request_count}</td>
                <td>
                  <span style={{ color: u.is_active ? 'var(--brand-green)' : 'var(--brand-red)', fontWeight: 600, fontSize: 13 }}>
                    {u.is_active ? '● Active' : '● Inactive'}
                  </span>
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(u.created_at).toLocaleDateString()}</td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditUser(u)}>✏️ Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u.id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {editUser && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditUser(null)}>
          <motion.div className="modal-box" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="modal-header">
              <h2>✏️ Edit User</h2>
              <button className="modal-close" onClick={() => setEditUser(null)}>✕</button>
            </div>
            <form onSubmit={saveUser}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-control" value={editUser.name} onChange={e => setEditUser({ ...editUser, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={editUser.email} onChange={e => setEditUser({ ...editUser, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-control" value={editUser.phone || ''} onChange={e => setEditUser({ ...editUser, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select className="form-control" value={editUser.plan_id || ''} onChange={e => setEditUser({ ...editUser, plan_id: e.target.value })}>
                  <option value="">No Plan</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={editUser.is_active ? '1' : '0'} onChange={e => setEditUser({ ...editUser, is_active: e.target.value === '1' })}>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>💾 Save Changes</button>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

// ===== CREATE USER =====
function CreateUser() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', plan_id: '' });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/admin/plans').then(res => setPlans(res.data.plans));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.plan_id) return toast.error('Name, email, plan required');
    setLoading(true);
    try {
      const res = await API.post('/admin/users', form);
      toast.success(res.data.message);
      setForm({ name: '', email: '', phone: '', plan_id: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); } finally { setLoading(false); }
  };

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="glass-card" style={{ maxWidth: 560 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>➕ Create New User</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-control" placeholder="User's full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Email Address *</label>
            <input className="form-control" type="email" placeholder="user@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <input className="form-control" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Assign Plan *</label>
            <select className="form-control" value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value })} required>
              <option value="">Select a plan</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name} — ${p.price}/mo</option>)}
            </select>
          </div>
          <div className="alert alert-info">
            💡 A random password will be generated and sent to the user's email along with their login credentials.
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading} style={{ minWidth: 180 }}>
            {loading ? '⏳ Creating...' : '🚀 Create User & Send Email'}
          </button>
        </form>
      </div>
    </motion.div>
  );
}

// ===== PLANS MANAGEMENT =====
function PlansManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null);

  useEffect(() => {
    API.get('/admin/plans').then(res => setPlans(res.data.plans)).finally(() => setLoading(false));
  }, []);

  const savePlan = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/admin/plans/${editPlan.id}`, { ...editPlan, features: Array.isArray(editPlan.features) ? editPlan.features : editPlan.features.split('\n').filter(Boolean) });
      toast.success('Plan updated!');
      setEditPlan(null);
      API.get('/admin/plans').then(res => setPlans(res.data.plans));
    } catch { toast.error('Failed to update plan'); }
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="pricing-grid">
        {plans.map((plan, i) => (
          <div key={plan.id} className={`pricing-card ${i === 1 ? 'featured' : ''}`}>
            <div className="plan-name">{plan.name}</div>
            <div className="plan-price">
              <span className="price-currency">$</span>
              <span className="price-amount text-gradient">{Number(plan.price).toLocaleString()}</span>
              <div className="price-period">/{plan.duration_days} days · {plan.request_limit} requests</div>
            </div>
            <ul className="plan-features">
              {plan.features.map((f, fi) => <li key={fi}><span className="feature-check">✓</span>{f}</li>)}
            </ul>
            <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setEditPlan({ ...plan, features: plan.features.join('\n') })}>
              ✏️ Edit Plan
            </button>
          </div>
        ))}
      </div>

      {editPlan && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditPlan(null)}>
          <motion.div className="modal-box" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="modal-header">
              <h2>✏️ Edit {editPlan.name} Plan</h2>
              <button className="modal-close" onClick={() => setEditPlan(null)}>✕</button>
            </div>
            <form onSubmit={savePlan}>
              {[
                { label: 'Plan Name', key: 'name' },
                { label: 'Description', key: 'description' },
                { label: 'Price ($)', key: 'price', type: 'number' },
                { label: 'Duration (days)', key: 'duration_days', type: 'number' },
                { label: 'Request Limit', key: 'request_limit', type: 'number' },
              ].map(f => (
                <div className="form-group" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input className="form-control" type={f.type || 'text'} value={editPlan[f.key]} onChange={e => setEditPlan({ ...editPlan, [f.key]: e.target.value })} />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Features (one per line)</label>
                <textarea className="form-control" value={editPlan.features} onChange={e => setEditPlan({ ...editPlan, features: e.target.value })} style={{ minHeight: 120 }} />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>💾 Save Plan</button>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

// ===== ORDERS =====
function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/payment/orders').then(res => setOrders(res.data.orders)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header">
          <h3>💰 All Orders ({orders.length})</h3>
          <div style={{ color: 'var(--brand-green)', fontWeight: 700, fontSize: 14 }}>
            Total: ${orders.filter(o => o.status === 'paid').reduce((s, o) => s + Number(o.amount), 0).toLocaleString()}
          </div>
        </div>
        {orders.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">💰</div><h3>No orders yet</h3></div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Order ID</th><th>Customer</th><th>Plan</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{o.razorpay_order_id || `#${o.id}`}</td>
                  <td>{o.customer_name}<br /><span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{o.customer_email}</span></td>
                  <td><span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--brand-primary)', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{o.plan_name}</span></td>
                  <td style={{ color: 'var(--brand-green)', fontWeight: 700 }}>${Number(o.amount).toLocaleString()}</td>
                  <td><span className={`status-badge ${o.status === 'paid' ? 'status-resolved' : o.status === 'failed' ? 'status-closed' : 'status-open'}`}>{o.status}</span></td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </motion.div>
  );
}

// ===== DEVELOPERS MANAGEMENT =====
function DevelopersManagement() {
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    loadDevelopers();
  }, []);

  const loadDevelopers = async () => {
    try {
      const res = await API.get('/developers/all');
      setDevelopers(res.data.developers);
    } catch {
      toast.error('Failed to load developers');
    } finally {
      setLoading(false);
    }
  };

  const saveDeveloper = async (e) => {
    e.preventDefault();
    if (!form.name || !form.role || !form.experience || !form.expertise) {
      return toast.error('Please fill required fields');
    }
    
    try {
      if (isEdit) {
        await API.put(`/developers/${form.id}`, form);
        toast.success('Developer updated!');
      } else {
        await API.post('/developers', form);
        toast.success('Developer added!');
      }
      setForm(null);
      loadDevelopers();
    } catch {
      toast.error('Failed to save developer');
    }
  };

  const deleteDeveloper = async (id) => {
    if (!window.confirm('Delete this developer?')) return;
    try {
      await API.delete(`/developers/${id}`);
      toast.success('Developer deleted');
      loadDevelopers();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header">
          <h3>👨‍💻 Manage Developers ({developers.length})</h3>
          <button className="btn btn-primary btn-sm" onClick={() => { setForm({ name: '', role: '', experience: '', expertise: '', skills: '', image_url: '', is_active: true }); setIsEdit(false); }}>
            ➕ Add Developer
          </button>
        </div>
        {developers.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">👨‍💻</div><h3>No developers added</h3></div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Developer</th><th>Role</th><th>Experience</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {developers.map(dev => (
                <tr key={dev.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {dev.image_url ? (
                        <img src={dev.image_url} alt={dev.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--brand-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                          {dev.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600 }}>{dev.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{dev.expertise.substring(0, 30)}...</div>
                      </div>
                    </div>
                  </td>
                  <td>{dev.role}</td>
                  <td>{dev.experience}</td>
                  <td>
                    <span style={{ color: dev.is_active ? 'var(--brand-green)' : 'var(--brand-red)', fontWeight: 600, fontSize: 13 }}>
                      {dev.is_active ? '● Active' : '● Inactive'}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setForm(dev); setIsEdit(true); }}>✏️</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteDeveloper(dev.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {form && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setForm(null)}>
          <motion.div className="modal-box" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="modal-header">
              <h2>{isEdit ? '✏️ Edit Developer' : '➕ Add Developer'}</h2>
              <button className="modal-close" onClick={() => setForm(null)}>✕</button>
            </div>
            <form onSubmit={saveDeveloper}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <input className="form-control" placeholder="e.g. Senior DevOps Eng." value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Experience *</label>
                  <input className="form-control" placeholder="e.g. 5+ Years" value={form.experience} onChange={e => setForm({ ...form, experience: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.is_active ? '1' : '0'} onChange={e => setForm({ ...form, is_active: e.target.value === '1' })}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Expertise * (Short description)</label>
                <input className="form-control" value={form.expertise} onChange={e => setForm({ ...form, expertise: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Skills (Comma separated)</label>
                <input className="form-control" placeholder="AWS, Docker, K8s..." value={form.skills} onChange={e => setForm({ ...form, skills: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Image URL</label>
                <input className="form-control" placeholder="https://..." value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                💾 {isEdit ? 'Save Changes' : 'Add Developer'}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

// ===== CONTACT ENQUIRIES =====
function ContactEnquiries() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMessage, setViewMessage] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const res = await API.get('/contact/all');
      setMessages(res.data.messages);
    } catch {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await API.delete(`/contact/${id}`);
      toast.success('Inquiry deleted');
      loadMessages();
      if (viewMessage && viewMessage.id === id) setViewMessage(null);
    } catch {
      toast.error('Failed to delete inquiry');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await API.put(`/contact/${id}/status`, { status });
      toast.success('Status updated');
      loadMessages();
      if (viewMessage && viewMessage.id === id) setViewMessage({ ...viewMessage, status });
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleRowClick = async (m) => {
    setViewMessage(m);
    if (m.status === 'unread') {
      await updateStatus(m.id, 'read');
    }
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header">
          <h3>📬 All Enquiries ({messages.length})</h3>
        </div>
        {messages.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📬</div><h3>No inquiries yet</h3></div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Name</th><th>Subject</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
            <tbody>
              {messages.map(m => (
                <tr key={m.id} style={{ cursor: 'pointer', background: m.status === 'unread' ? 'rgba(99,102,241,0.05)' : 'transparent' }}>
                  <td onClick={() => handleRowClick(m)}>
                    <div style={{ fontWeight: m.status === 'unread' ? 700 : 500 }}>{m.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{m.email}</div>
                  </td>
                  <td onClick={() => handleRowClick(m)} style={{ fontWeight: m.status === 'unread' ? 700 : 500 }}>{m.subject || '(No Subject)'}</td>
                  <td onClick={() => handleRowClick(m)}>
                    <span className={`status-badge ${m.status === 'unread' ? 'status-open' : m.status === 'read' ? 'status-in_progress' : 'status-resolved'}`}>
                      {m.status}
                    </span>
                  </td>
                  <td onClick={() => handleRowClick(m)} style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(m.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); handleRowClick(m); }}>👀</button>
                    <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); deleteMessage(m.id); }} style={{ marginLeft: 6 }}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {viewMessage && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewMessage(null)}>
          <motion.div className="modal-box" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h2>Message from {viewMessage.name}</h2>
              <button className="modal-close" onClick={() => setViewMessage(null)}>✕</button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Email Address:</div>
                  <a href={`mailto:${viewMessage.email}`} style={{ fontWeight: 600, color: 'var(--brand-primary)', textDecoration: 'none' }}>{viewMessage.email}</a>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Received:</div>
                  <div style={{ fontWeight: 600 }}>{new Date(viewMessage.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Subject:</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{viewMessage.subject || '(No Subject)'}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 8 }}>Message:</div>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', padding: 16, borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {viewMessage.message}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: 20 }}>
              <select className="form-control" style={{ width: 'auto' }} value={viewMessage.status} onChange={(e) => updateStatus(viewMessage.id, e.target.value)}>
                <option value="unread">Mark Unread</option>
                <option value="read">Mark Read</option>
                <option value="replied">Mark Replied</option>
              </select>
              <a href={`mailto:${viewMessage.email}?subject=RE: ${viewMessage.subject || 'Your Inquiry'}`} className="btn btn-primary">
                📧 Reply via Email
              </a>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

// ===== SUBADMINS MANAGEMENT =====
function SubadminsManagement() {
  const [subadmins, setSubadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ id: null, name: '', email: '', password: '', permissions: [] });

  const PERMISSIONS = [
    { id: 'requests', label: 'All Tasks (Requests)' },
    { id: 'users', label: 'Users' },
    { id: 'plans', label: 'Plans' },
    { id: 'orders', label: 'Orders' },
    { id: 'developers', label: 'Developers' },
    { id: 'contact', label: 'Contact Enquiries' }
  ];

  useEffect(() => { loadSubadmins(); }, []);

  const loadSubadmins = async () => {
    try {
      const res = await API.get('/subadmins');
      setSubadmins(res.data.subadmins);
    } catch { toast.error('Failed to load sub-admins'); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (form.id) {
        await API.put(`/subadmins/${form.id}`, form);
        toast.success('Sub-Admin updated');
      } else {
        await API.post('/subadmins', form);
        toast.success('Sub-Admin created');
      }
      setShowModal(false);
      loadSubadmins();
    } catch(err) {
      toast.error(err.response?.data?.message || 'Error saving sub-admin');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete sub-admin?')) return;
    try {
      await API.delete(`/subadmins/${id}`);
      toast.success('Sub-Admin deleted');
      loadSubadmins();
    } catch { toast.error('Error deleting'); }
  };

  const togglePermission = (permId) => {
    setForm(prev => {
      const p = prev.permissions.includes(permId) ? prev.permissions.filter(x => x !== permId) : [...prev.permissions, permId];
      return { ...prev, permissions: p };
    });
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner"/></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header">
          <h3>🛡️ Sub-Admins ({subadmins.length})</h3>
          <button className="btn btn-primary btn-sm" onClick={() => { setForm({id: null, name: '', email: '', password: '', permissions: []}); setShowModal(true); }}>
            + Create Sub-Admin
          </button>
        </div>
        {subadmins.length === 0 ? (
          <div className="empty-state"><h3>No sub-admins</h3></div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Name</th><th>Email</th><th>Permissions</th><th>Actions</th></tr></thead>
            <tbody>
              {subadmins.map(s => {
                 let perms = [];
                 try { perms = typeof s.permissions === 'string' ? JSON.parse(s.permissions) : s.permissions; } catch(e){}
                 return (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td><span className="badge" style={{margin:0, padding: '2px 8px'}}>{perms ? perms.length : 0} modules</span></td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setForm({id: s.id, name: s.name, email: s.email, password: '', permissions: perms || []}); setShowModal(true); }}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)} style={{marginLeft: 8}}>Delete</button>
                    </td>
                  </tr>
                 )
              })}
            </tbody>
          </table>
        )}
      </div>
      
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <motion.div className="modal-box" initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}}>
            <div className="modal-header">
              <h2>{form.id ? 'Edit Sub-Admin' : 'Create Sub-Admin'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <input className="form-control" placeholder="Name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} required />
              </div>
              <div className="form-group">
                <input className="form-control" type="email" placeholder="Email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} required />
              </div>
              <div className="form-group">
                <input className="form-control" type="password" placeholder={form.id ? "New Password (leave blank to keep)" : "Password"} value={form.password} onChange={e=>setForm({...form, password: e.target.value})} required={!form.id} />
              </div>
              <div className="form-group">
                <label className="form-label">Module Permissions</label>
                <div style={{display: 'flex', flexDirection: 'column', gap: 10, background: 'rgba(255,255,255,0.02)', padding: 16, borderRadius: 8}}>
                   {PERMISSIONS.map(p => (
                     <label key={p.id} style={{display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer'}}>
                       <input type="checkbox" style={{width: 16, height: 16}} checked={form.permissions.includes(p.id)} onChange={() => togglePermission(p.id)} />
                       <span>{p.label}</span>
                     </label>
                   ))}
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{width: '100%', justifyContent: 'center'}}>Save Sub-Admin</button>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

// ===== MAIN ADMIN =====
export default function Admin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin' && user.role !== 'subadmin') return <Navigate to="/dashboard" />;

  useEffect(() => {
    API.get('/admin/stats').then(res => {
      setStats(res.data.stats);
      setRecentRequests(res.data.recentRequests);
    });
  }, []);

  const handleViewRequest = (req) => {
    setSelectedRequest(req);
    setActiveTab('request-detail');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return <AdminOverview stats={stats} recentRequests={recentRequests} onViewRequest={handleViewRequest} />;
      case 'requests': return <AdminRequests onViewRequest={handleViewRequest} />;
      case 'request-detail': return <AdminRequestDetail request={selectedRequest} onBack={() => { setSelectedRequest(null); setActiveTab('requests'); }} />;
      case 'users': return <UsersManagement />;
      case 'create-user': return <CreateUser />;
      case 'plans': return <PlansManagement />;
      case 'orders': return <Orders />;
      case 'developers': return <DevelopersManagement />;
      case 'contact': return <ContactEnquiries />;
      case 'subadmins': return <SubadminsManagement />;
      default: return <AdminOverview stats={stats} recentRequests={recentRequests} onViewRequest={handleViewRequest} />;
    }
  };

  const titles = {
    overview: '📊 Admin Dashboard',
    requests: '📋 All Tasks',
    'request-detail': '📋 Task Details',
    users: '👥 Users',
    'create-user': '➕ Create User',
    plans: '💳 Plans',
    orders: '💰 Orders',
    developers: '👨‍💻 Developers',
    contact: '📬 Contact Enquiries',
    subadmins: '🛡️ Sub-Admins Management'
  };

  return (
    <div className="dashboard-layout">
      <Sidebar
        active={activeTab === 'request-detail' ? 'requests' : activeTab}
        onNavigate={(tab) => { setActiveTab(tab); setSelectedRequest(null); }}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      <main className="dashboard-main">
        <div className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button style={{ background: 'var(--bg-glass)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 18 }} onClick={() => setMobileOpen(true)}>
              ☰
            </button>
            <h1 className="dashboard-title">{titles[activeTab] || 'Admin'}</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700 }}>
              ⚡ Admin
            </span>
          </div>
        </div>

        <div className="dashboard-content">
          <AnimatePresence mode="wait">
            <div key={activeTab}>
              {renderContent()}
            </div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
